// Cube cell helpers.
//
// Cube coordinates render in two pricing-model shapes:
//   - bureau_band_x_housing_x_channel: "950_plus / HomeOwner / hLoan"
//   - grade_x_subgrade_x_channel:      "A1 / hLoan"
//
// The interface composes coordinates for click-to-stage edits and
// parses them for the Staged Changes panel. Same shape as the
// catalogue-client's parseCoordinates so the proxy Worker accepts
// what the interface emits.

import type { CubeCellValue, RateCube } from './types';
import { CREDIT_RATING_LEVEL_METADATA_KEYS } from './staged-edits';

export interface BureauCoordinate {
  kind: 'bureau';
  bureauBand: string;
  housing: string;
  channel: string;
}

export interface GradeCoordinate {
  kind: 'grade';
  gradeSubgrade: string;
  channel: string;
}

export type CubeCoordinate = BureauCoordinate | GradeCoordinate;

export function formatCoordinate(coord: CubeCoordinate): string {
  if (coord.kind === 'bureau') {
    return `${coord.bureauBand} / ${coord.housing} / ${coord.channel}`;
  }
  return `${coord.gradeSubgrade} / ${coord.channel}`;
}

export function parseBureauCoordinate(s: string): BureauCoordinate {
  const parts = s.split(/\s*\/\s*/);
  if (parts.length !== 3) throw new Error(`Expected bureau coordinate with 3 parts; got "${s}"`);
  return {
    kind: 'bureau',
    bureauBand: parts[0] ?? '',
    housing: parts[1] ?? '',
    channel: parts[2] ?? ''
  };
}

export function parseGradeCoordinate(s: string): GradeCoordinate {
  const parts = s.split(/\s*\/\s*/);
  if (parts.length !== 2) throw new Error(`Expected grade coordinate with 2 parts; got "${s}"`);
  return { kind: 'grade', gradeSubgrade: parts[0] ?? '', channel: parts[1] ?? '' };
}

export function readCell(cube: RateCube, coord: CubeCoordinate): CubeCellValue | undefined {
  if (coord.kind === 'bureau') {
    return cube[coord.bureauBand]?.[coord.housing]?.[coord.channel];
  }
  const node = cube[coord.gradeSubgrade] as unknown as Record<string, CubeCellValue> | undefined;
  return node?.[coord.channel];
}

export function bureauBandIds(cube: RateCube): string[] {
  return Object.keys(cube);
}

// Returns the housing ids for a given bureau band, excluding any
// credit-rating-level metadata keys (e.g. establishment_fee_override,
// which sits alongside the housing nodes at the cube_outer_level node
// per schema/calculator-source-of-truth.schema.json). Mirrors the
// same filter staged-edits.ts, surface-projector.ts, validate.mjs,
// and catalogue-client.mjs apply at every other site that iterates
// the cube_outer_level children. Without this filter, the editor
// renders the metadata key as a phantom housing column with a "—"
// data row and the Dashboard tile's housings count is inflated.
export function housingIds(cube: RateCube, bureauBand: string): string[] {
  return Object.keys(cube[bureauBand] || {}).filter(
    (k) => !CREDIT_RATING_LEVEL_METADATA_KEYS.has(k)
  );
}

export function channelIds(cube: RateCube): string[] {
  const seen = new Set<string>();
  for (const a of Object.values(cube)) {
    for (const b of Object.values(a)) {
      if (b && typeof b === 'object') {
        const inner = b as unknown as Record<string, unknown>;
        for (const key of Object.keys(inner)) {
          // Inner may be a CubeCellValue (grade model) or another nesting (bureau model).
          if (typeof inner[key] === 'object' && inner[key] !== null && 'interest_rate_pa' in (inner[key] as Record<string, unknown>)) {
            seen.add(key);
          }
        }
      }
    }
  }
  return [...seen].sort();
}

export function gradeSubgradeIds(cube: RateCube): string[] {
  return Object.keys(cube);
}

// Cell counts for the "7 × 3 hsg × 2 chan" Dashboard summary.
// Excludes credit-rating-level metadata keys (e.g.
// establishment_fee_override) from the housings count — see the
// housingIds helper above for the rationale.
export function cubeDimensions(
  cube: RateCube,
  pricingModel: 'bureau_band_x_housing_x_channel' | 'grade_x_subgrade_x_channel'
): { bands?: number; housings?: number; channels: number; grades?: number; totalCells: number } {
  if (pricingModel === 'bureau_band_x_housing_x_channel') {
    const bands = Object.keys(cube).length;
    const firstBand = bands > 0 ? Object.values(cube)[0] : undefined;
    const housingKeys = firstBand
      ? Object.keys(firstBand).filter((k) => !CREDIT_RATING_LEVEL_METADATA_KEYS.has(k))
      : [];
    const housings = housingKeys.length;
    const firstHousingKey = housingKeys[0];
    const firstHousing = firstBand && firstHousingKey
      ? (firstBand[firstHousingKey] as unknown as Record<string, CubeCellValue> | undefined)
      : undefined;
    const channels = firstHousing ? Object.keys(firstHousing).length : 0;
    return { bands, housings, channels, totalCells: bands * housings * channels };
  }
  const grades = Object.keys(cube).length;
  const firstGrade = grades > 0 ? Object.values(cube)[0] as Record<string, CubeCellValue> | undefined : undefined;
  const channelKeys = firstGrade
    ? Object.keys(firstGrade).filter((k) => !CREDIT_RATING_LEVEL_METADATA_KEYS.has(k))
    : [];
  const channels = channelKeys.length;
  return { grades, channels, totalCells: grades * channels };
}
